"use client";

import type { QuizMatchingEntryData } from "@/types/storyboard";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import confetti from "canvas-confetti";
import { ArrowRight, ClipboardCheck, GripVertical } from "lucide-react";
import DraggableTerm, {
  TERM_CHROME,
} from "@/components/storyboard/quiz/matching-definition/DraggableTerm";
import DroppableSentenceSlot from "@/components/storyboard/quiz/matching-definition/DroppableSentenceSlot";
import QuizFeedback from "@/components/storyboard/quiz/QuizFeedback";
import { CourseMarkdown } from "@/components/storyboard/CourseMarkdown";
import TaskInstructionHint from "@/components/storyboard/TaskInstructionHint";
import QuizCheckNextAction from "@/components/storyboard/quiz/QuizCheckNextAction";
import QuizEntryCounter from "@/components/storyboard/quiz/QuizEntryCounter";
import { buildMatchingEntryReview } from "@/components/storyboard/quiz/review/buildEntryReview";
import { cn } from "@/lib/utils";
import type { InteractiveElementScore } from "@/lib/interactiveElementState";
import { useScrollToTopOnSubmit } from "@/hooks/useScrollToTopOnSubmit";
import type { QuizReview, QuizReviewItem } from "@/lib/quizReview";
import {
  advanceAfterCollect,
  collectsWithoutVerdict,
  type HiddenVerdictProps,
} from "@/lib/quizCollect";

type AssignmentMap = Record<string, string | null>;

export interface QuizMatchingState {
  currentIndex: number;
  isSubmitted: boolean;
  isCorrect: boolean | null;
  assignments: AssignmentMap;
  activeTerm: string | null;
  correctCount: number;
}

export const shuffleArray = <T,>(arr: T[]): T[] => {
  const shuffled = [...arr];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

export const transformEntries = (entries: QuizMatchingEntryData[]) => {
  return (entries ?? []).map((entry) => ({
    definitionSentences: shuffleArray(
      (entry.pairs ?? []).map((pair) => pair.secondPart)
    ),
    definitions: (entry.pairs ?? []).map((pair) => pair.firstPart),
    instruction: entry.instruction,
    userFeedback: entry.userFeedback,
  }));
};

export interface QuizMatchingProps extends HiddenVerdictProps {
  entries: QuizMatchingEntryData[];
  onValidate: (
    pairs: { firstPart: string; secondPart: string }[],
    currentIndex: number
  ) => Promise<boolean> | boolean;
  onComplete?: (score: InteractiveElementScore, review: QuizReview) => void;
  initialPartial?: QuizMatchingState;
  initialIndex?: number;
  onPartialChange?: (partial: QuizMatchingState, index: number) => void;
}

interface MatchingTermDragOverlayProps {
  term: string | null;
}

/**
 * Floating term preview under the pointer — opaque underlay so selected chrome
 * doesn’t read as glass over page content.
 */
const MatchingTermDragOverlay = ({ term }: MatchingTermDragOverlayProps) => {
  if (term == null) return null;

  return (
    <div className="h-full w-full rounded-xl bg-background shadow-lg">
      <div
        className={cn(
          "flex h-full w-full items-center gap-2 rounded-xl border-2 px-4 py-2",
          TERM_CHROME.selected
        )}
      >
        <GripVertical
          className={cn("h-3.5 w-3.5 shrink-0", TERM_CHROME.grip)}
          aria-hidden="true"
        />
        <span className="min-w-0 flex-1 font-semibold whitespace-normal">
          {term}
        </span>
      </div>
    </div>
  );
};

interface MatchingTermPoolProps {
  terms: string[];
}

/**
 * Left-column term sources — empty “Alles zugeordnet” state when every term
 * has been dropped into a sentence slot.
 */
const MatchingTermPool = ({ terms }: MatchingTermPoolProps) => {
  if (terms.length === 0) {
    return (
      <div className="flex animate-pop-in flex-col items-center justify-center gap-1.5">
        <ClipboardCheck
          className="h-16 w-16 text-muted-foreground"
          aria-hidden="true"
        />
        <p className="text-center font-semibold text-primary">
          Alles zugeordnet
        </p>
      </div>
    );
  }

  return (
    <div className="flex w-full min-w-0 flex-col gap-2">
      {terms.map((term) => (
        <DraggableTerm key={term} term={term} />
      ))}
    </div>
  );
};

const QuizMatching = ({
  entries,
  onValidate,
  onComplete,
  initialPartial,
  initialIndex,
  onPartialChange,
  revealFeedback,
  advanceLabel,
  onAdvance,
}: QuizMatchingProps) => {
  const [transformedEntries] = useState(() => transformEntries(entries));

  const [state, setState] = useState<QuizMatchingState>(() => {
    if (initialPartial) {
      return initialPartial;
    }
    const startIndex = initialIndex ?? 0;
    const firstEntry = transformedEntries[startIndex];
    const firstSentences = firstEntry?.definitionSentences ?? [];
    return {
      currentIndex: startIndex,
      isSubmitted: false,
      isCorrect: null,
      assignments: Object.fromEntries(firstSentences.map((s) => [s, null])),
      activeTerm: null,
      correctCount: 0,
    };
  });

  const hasInteractedRef = useRef<boolean>(initialPartial != null);
  const reviewRef = useRef<QuizReviewItem[]>([]);

  useEffect(() => {
    if (!hasInteractedRef.current) return;
    onPartialChange?.(state, state.currentIndex);
  }, [state, onPartialChange]);

  const { currentIndex, isSubmitted, isCorrect, assignments, activeTerm } =
    state;

  const contentRef = useScrollToTopOnSubmit<HTMLDivElement>(isSubmitted);

  const currentEntry = transformedEntries[currentIndex];

  const sentences = useMemo(
    () => currentEntry?.definitionSentences ?? [],
    [currentEntry?.definitionSentences]
  );

  const terms = useMemo(
    () => currentEntry?.definitions ?? [],
    [currentEntry?.definitions]
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } })
  );

  const usedTerms = useMemo(() => {
    const set = new Set<string>();
    Object.values(assignments).forEach((t) => t && set.add(t));
    return set;
  }, [assignments]);

  const availableTerms = useMemo(
    () => terms.filter((t) => !usedTerms.has(t)),
    [terms, usedTerms]
  );

  const allAssigned = useMemo(
    () => sentences.every((s) => assignments[s] !== null),
    [sentences, assignments]
  );

  if (!entries || entries.length === 0 || !currentEntry) {
    return null;
  }

  const clearSlot = (sentence: string) => {
    setState((prev) => ({
      ...prev,
      assignments: { ...prev.assignments, [sentence]: null },
    }));
  };

  const buildSubmittedPairs = () =>
    sentences
      .map((sentence) => {
        const term = assignments[sentence];
        if (!term) return null;
        return { firstPart: term, secondPart: sentence };
      })
      .filter(Boolean) as { firstPart: string; secondPart: string }[];

  const onDragEnd = (e: DragEndEvent) => {
    setState((prev) => ({
      ...prev,
      activeTerm: null,
    }));

    const overId = e.over?.id as string | undefined;
    const term = e.active.data.current?.term as string | undefined;
    if (!overId || !term) return;
    if (!overId.startsWith("slot:")) return;

    const sentence = overId.replace("slot:", "");
    if (assignments[sentence]) return;

    hasInteractedRef.current = true;

    setState((prev) => {
      const nextAssignments = { ...prev.assignments };

      Object.keys(nextAssignments).forEach((slotSentence) => {
        if (nextAssignments[slotSentence] === term) {
          nextAssignments[slotSentence] = null;
        }
      });

      nextAssignments[sentence] = term;
      return { ...prev, assignments: nextAssignments };
    });
  };

  const handleSubmit = async () => {
    const pairs = buildSubmittedPairs();
    if (collectsWithoutVerdict(revealFeedback)) {
      await advanceAfterCollect(
        () => Promise.resolve(onValidate(pairs, currentIndex)),
        onAdvance
      );
      return;
    }
    const correct = await onValidate(pairs, currentIndex);
    const correctCount = state.correctCount + (correct ? 1 : 0);

    reviewRef.current[currentIndex] = buildMatchingEntryReview(
      entries[currentIndex],
      assignments,
      correct
    );

    hasInteractedRef.current = true;
    setState((prev) => ({
      ...prev,
      isSubmitted: true,
      isCorrect: correct,
      correctCount,
    }));

    if (currentIndex === entries.length - 1) {
      onComplete?.(
        { correct: correctCount, total: entries.length },
        { items: reviewRef.current }
      );
      if (correct) {
        confetti({
          particleCount: 150,
          spread: 80,
          origin: { y: 0.6 },
        });
      }
    }
  };

  const handleNext = () => {
    if (currentIndex < entries.length - 1) {
      const nextEntry = transformedEntries[currentIndex + 1];
      const nextSentences = nextEntry?.definitionSentences ?? [];
      setState((prev) => ({
        ...prev,
        currentIndex: prev.currentIndex + 1,
        isSubmitted: false,
        isCorrect: null,
        assignments: Object.fromEntries(nextSentences.map((s) => [s, null])),
        activeTerm: null,
      }));
    }
  };

  return (
    <div className="flex min-w-0 flex-col">
      <QuizEntryCounter
        currentIndex={currentIndex}
        total={transformedEntries.length}
      >
        <div className="prose quiz-question max-w-full font-semibold text-primary">
          <CourseMarkdown>{currentEntry.instruction}</CourseMarkdown>
        </div>
      </QuizEntryCounter>

      <div ref={contentRef} className="flex flex-col gap-4 pb-4 pt-8">
        <QuizFeedback
          isSubmitted={isSubmitted}
          isCorrect={isCorrect}
          positiveFeedback={currentEntry.userFeedback?.positive}
          negativeFeedback={currentEntry.userFeedback?.negative}
        />

        <div className="flex flex-col gap-4">
          <TaskInstructionHint>
            Ziehe die Begriffe zu den passenden Beschreibungen
          </TaskInstructionHint>
          <div className="min-w-0 rounded-xl border bg-white p-5">
            <DndContext
              sensors={sensors}
              collisionDetection={pointerWithin}
              onDragStart={(e) =>
                setState((prev) => ({
                  ...prev,
                  activeTerm: e.active.data.current?.term ?? null,
                }))
              }
              onDragCancel={() =>
                setState((prev) => ({
                  ...prev,
                  activeTerm: null,
                }))
              }
              onDragEnd={onDragEnd}
            >
              <div
                className={cn(
                  "grid min-w-0 grid-cols-1 gap-4",
                  availableTerms.length === 0
                    ? "items-stretch lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-8"
                    : "items-center lg:grid-cols-[minmax(0,0.9fr)_auto_minmax(0,1.1fr)] lg:gap-6"
                )}
              >
                <div className="flex min-h-0 min-w-0 items-center justify-center">
                  <MatchingTermPool terms={availableTerms} />
                </div>

                {availableTerms.length > 0 && (
                  <div
                    className="hidden items-center justify-center lg:flex"
                    aria-hidden="true"
                  >
                    <ArrowRight
                      className="h-10 w-10 text-primary"
                      strokeWidth={1.5}
                      aria-hidden="true"
                    />
                  </div>
                )}

                <div className="min-w-0 space-y-3">
                  {sentences.map((sentence) => (
                    <DroppableSentenceSlot
                      key={sentence}
                      sentence={sentence}
                      assignedTerm={assignments[sentence]}
                      onClear={() => clearSlot(sentence)}
                      highlightAsDropTarget={
                        activeTerm != null && !assignments[sentence]
                      }
                    />
                  ))}
                </div>
              </div>

              {createPortal(
                <DragOverlay dropAnimation={null}>
                  <MatchingTermDragOverlay term={activeTerm} />
                </DragOverlay>,
                document.body
              )}
            </DndContext>
          </div>
        </div>
      </div>

      <div className="shrink-0 pt-4">
        <div className="flex justify-center">
          <QuizCheckNextAction
            isSubmitted={isSubmitted}
            hasNextQuestion={currentIndex < entries.length - 1}
            canSubmit={allAssigned}
            onSubmit={handleSubmit}
            onNext={handleNext}
            submitLabel={
              collectsWithoutVerdict(revealFeedback) ? advanceLabel : undefined
            }
          />
        </div>
      </div>
    </div>
  );
};

export default QuizMatching;
